import { ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, afterNextRender, inject, signal, computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { form, required } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { arvonMuutokset, asetaArvo, nollaa } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

import { AsiakkaanTuote } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'

import { TuotePoistoVarmistusDialog, TuotePoistoVarmistusDialogData } from './tuote-poisto-varmistus.dialog'

import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface TuoteLomake {
  nimi: string
  hinta: number | null
}

const TYHJA_LOMAKE: TuoteLomake = { nimi: '', hinta: null }

@Component({
  selector: 'app-tuote',
  templateUrl: './tuote.component.html',
  styleUrls: ['./tuote.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TuoteComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput
  @ViewChild(MatInput, { static: true }) hintaInput: MatInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  lomake = form(signal<TuoteLomake>(TYHJA_LOMAKE), polku => {
    required(polku.nimi)
  })
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('tuote', this.lomake)
  })

  tuote: AsiakkaanTuote = null

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private ladataanService: LadataanService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    this.tuote = laskuKopioija.annaUusiTuote()
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngOnInit() {
    // Bind to changes
    arvonMuutokset(this.lomake.nimi, { injector: this._injector }).subscribe(value => {
      if (this.tuote) {
        this.tuote.nimi = this.validationService.processValue(value)
      }
    })
    arvonMuutokset(this.lomake.hinta, { injector: this._injector }).subscribe(value => {
      if (this.tuote) {
        this.tuote.hinta = value
      }
    })

    // Prepare edit
    this.route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data: { tuote: AsiakkaanTuote }) => {
      nollaa(this.lomake, TYHJA_LOMAKE)
      this.tuote = this.laskuKopioija.copyTuoteMuokattavaksi(data.tuote)
      asetaArvo(this.lomake.nimi, this.tuote.nimi ?? '')
      asetaArvo(this.lomake.hinta, this.tuote.hinta)
      this._changeDetectorRef.markForCheck()
    })

    // Signal Forms asettaa kentän käytöstilan ensimmäisessä muutostunnistuksessa, mikä nollaa Materialin kentän kohdistustilan
    afterNextRender(() => this.nimiInput.focus(), { injector: this._injector })
  }

  peruuta() {
    this.router.navigate(['/laskutus/tuotteet'])
  }

  poista() {
    const data: TuotePoistoVarmistusDialogData = {
      tuote: this.tuote,
    }
    this.dialog
      .open(TuotePoistoVarmistusDialog, { data: data })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.router.navigate(['/laskutus/tuotteet'])
        }
      })
  }

  save() {
    if (!this.lomake().valid()) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.ladataanService.aloitaLataaminen()
    this.laskuService
      .updateTuotteet([this.tuote])
      .then(result => {
        this._changeDetectorRef.markForCheck()
        this.ladataanService.lopetaLataaminen()
        this.router.navigate(['/laskutus/tuotteet'])
      })
      .catch(err => {
        this.ladataanService.lopetaLataaminen()
        this.errorHandler.handleError(err)
      })
  }
}
